import {
  type CampaignReport,
  type CampaignReportParams,
  type PartnerCampaign,
  type PlacementSlot,
} from '@/api/routes/partners/partners.campaigns.api';
import { isOfferActionKind, offerActionDefaultLabel } from '@/domain/offers/utils';
import {
  type DailyClicks,
  type ReportActionRow,
  type ReportPeriodDays,
  type ReportSliceRow,
} from '@/domain/results/types';
import { getActiveLocale } from '@/i18n/activeLocale';
import { translateFile, translatedMap } from '@/i18n';

export const REPORT_PERIODS: ReportPeriodDays[] = [7, 14, 30, 90];

export const DEFAULT_REPORT_PERIOD: ReportPeriodDays = 30;

export const LEADS_PAGE_SIZE = 20;

const DAY_MS = 24 * 60 * 60 * 1000;

export const placementSlotLabel = translatedMap('domain.results.utils', {
  marketplace_top: 'slot.marketplace_top',
  body_metrics: 'slot.body_metrics',
  profile: 'slot.profile',
  planning: 'slot.planning',
  plus_club: 'slot.plus_club',
} as const);

/** The window in the URL (`?days=`), falling back to the default for anything unknown. */
export const parseReportPeriod = (value: string | null): ReportPeriodDays =>
  REPORT_PERIODS.find((days) => String(days) === value) ?? DEFAULT_REPORT_PERIOD;

/** `from`/`to` of the last N days up to now, as the report endpoint reads them. */
export const toReportParams = (days: ReportPeriodDays, now = new Date()): CampaignReportParams => {
  // Rounded to the hour so the query key stays stable while the page is open.
  const to = new Date(Math.ceil(now.getTime() / 3_600_000) * 3_600_000);
  return { from: new Date(to.getTime() - days * DAY_MS).toISOString(), to: to.toISOString() };
};

/** The campaign in the URL when it is one of the partner's, else the first one on air, else the newest. */
export const pickCampaign = (
  campaigns: PartnerCampaign[],
  requestedId: string | null,
): PartnerCampaign | undefined =>
  campaigns.find((campaign) => campaign.id === requestedId) ??
  campaigns.find((campaign) => campaign.status === 'live') ??
  campaigns[0];

/** "18.420". */
export const formatCount = (value: number | null): string =>
  value === null ? '—' : new Intl.NumberFormat(getActiveLocale()).format(value);

/** "4,0%"; a dash without impressions. */
export const formatCtr = (ctr: number | null): string =>
  ctr === null
    ? '—'
    : new Intl.NumberFormat(getActiveLocale(), {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(ctr);

const formatDate = (value: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(getActiveLocale(), options)
    .format(new Date(value))
    .replace('.', '')
    .replace(/ de /g, ' ');

/** "1 out 2026 a 30 nov 2026", or "Desde 1 out 2026" without an end. */
export function formatCampaignPeriod(
  campaign: Pick<PartnerCampaign, 'startsAt' | 'endsAt'>,
): string {
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const start = formatDate(campaign.startsAt, options);
  return campaign.endsAt
    ? translateFile('domain.results.utils', 'period.range', {
        from: start,
        to: formatDate(campaign.endsAt, options),
      })
    : translateFile('domain.results.utils', 'period.since', { from: start });
}

/** "Marketplace, Corpo e Planejamento". */
export const formatSlots = (slots: PlacementSlot[]): string => {
  const labels = slots.map((slot) => placementSlotLabel[slot]);
  if (labels.length <= 1) return labels.join('');
  return translateFile('domain.results.utils', 'slotsJoin', {
    list: labels.slice(0, -1).join(', '),
    last: labels[labels.length - 1],
  });
};

/** "11 out" for a `YYYY-MM-DD` day of the report (São Paulo days). */
export const formatReportDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(getActiveLocale(), { day: 'numeric', month: 'short' })
    .format(new Date(year, month - 1, date))
    .replace('.', '')
    .replace(' de ', ' ');
};

/** Clicks per reported day, oldest first; hidden days carry `null`. */
export const getDailyClicks = (report: Pick<CampaignReport, 'byDay'>): DailyClicks[] =>
  [...report.byDay]
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((slice) => ({ day: slice.day, clicks: slice.hidden ? null : slice.clicks }));

const toCounts = (
  slice: CampaignReport['bySlot'][number] | CampaignReport['byVariant'][number],
) => ({
  impressions: slice.impressions,
  reach: slice.reach,
  clicks: slice.clicks,
  dismisses: slice.dismisses,
  leads: slice.leads,
  couponCopies: slice.couponCopies,
  ctr: slice.ctr,
});

/** "Por lugar no app": ordered by clicks, slices under 20 people last. */
export const getSlotRows = (report: Pick<CampaignReport, 'bySlot'>): ReportSliceRow[] =>
  [...report.bySlot]
    .sort((a, b) => (b.clicks ?? -1) - (a.clicks ?? -1))
    .map((slice) => ({
      key: slice.slot,
      label: placementSlotLabel[slice.slot],
      hidden: slice.hidden,
      counts: toCounts(slice),
    }));

/** "Por variante": only worth a table when the campaign runs an A/B test. */
export const getVariantRows = (report: Pick<CampaignReport, 'byVariant'>): ReportSliceRow[] =>
  report.byVariant.length < 2
    ? []
    : report.byVariant.map((slice) => ({
        key: slice.variantKey,
        label: slice.variantKey === '-' ? '—' : slice.variantKey,
        hidden: slice.hidden,
        counts: toCounts(slice),
      }));

/**
 * A button kind by its default text ("Ir para o site"); `-` (a click without a button kind) as a
 * dash, like the variants; a kind the portal does not know, as sent.
 */
export const reportActionLabel = (action: string): string => {
  if (isOfferActionKind(action)) return offerActionDefaultLabel[action];
  return action === '-' ? '—' : action;
};

/** "Cliques por botão": most clicked first, buttons under 20 people last. */
export const getActionRows = (report: Pick<CampaignReport, 'byAction'>): ReportActionRow[] =>
  [...report.byAction]
    .sort((a, b) => (b.clicks ?? -1) - (a.clicks ?? -1))
    .map((slice) => ({
      key: slice.action,
      label: reportActionLabel(slice.action),
      hidden: slice.hidden,
      clicks: slice.clicks,
      people: slice.people,
    }));

/** "leads-Avaliação de hidratação.csv". */
export const leadsCsvFilename = (campaign: Pick<PartnerCampaign, 'name'>) =>
  `leads-${campaign.name.replace(/[\\/:*?"<>|]+/g, '-')}.csv`;

/** "11 out · 07:42". */
export const formatLeadDate = (value: string): string => {
  const date = new Date(value);
  const day = new Intl.DateTimeFormat(getActiveLocale(), { day: 'numeric', month: 'short' })
    .format(date)
    .replace('.', '')
    .replace(' de ', ' ');
  const time = new Intl.DateTimeFormat(getActiveLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day} · ${time}`;
};
